/* eslint-disable import/no-relative-packages */
import React, { useState, useRef } from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import RichText from "@qlik/qlik/src/components/RichText";
import { setValueInStorage } from "@qlik/qlik/src/lib/util/browser-apis";
import {
    RESOURCE_GATED_FORM_EXPIRY,
    RESOURCE_GATED_REDIRECTING_TEXT,
    RESOURCE_GATED_ACCESS_TEXT,
} from "@qlik/qlik/src/lib/constants/resource-constants";
import { TYPE_MARKETO_FORM } from "@qlik/qlik/src/lib/constants/contentful-types";
import {
    scrollToElement,
    getMicrocopyValueFromKeys,
    renderSanitizedHtmlContent,
} from "@qlik/qlik/src/lib/util/helpers";
import useFormSession from "@qlik/qlik/src/hooks/useFormSession";
import { navigate } from "gatsby";
import AccessForm from "./AccessForm";
import * as styles from "./styles.module.css";

/**
 * GatedContent is a React component that renders a section of gated content.
 * Gated content is content that requires some form of authentication or action (like form submission) to access.
 *
 * @component
 * @param {Object} props The properties passed to the component.
 * @param {Object} props.data The data for the gated content section.
 * @param {string} props.data.backgroundColor The background color for the section.
 * @param {Object[]} props.data.backgroundImage The background image for the section.
 * @param {Object} props.data.additionalConfiguration Additional configuration for the section.
 * @param {Object[]} props.data.cardItems The card items to be displayed in the section.
 * @param {string} props.successUrl The URL to navigate to upon successful form submission.
 *
 * @returns {JSX.Element} A section of gated content.
 */
const GatedContent = ({ data, successUrl }) => {
    const { backgroundColor, backgroundImage, additionalConfiguration, cardItems = [], customId } = data ?? {};
    const sectionPaddingClassName = getSpacingClassName(additionalConfiguration, "py-15 lg:py-30");
    const { sectionColorMapping } = getColorClassName(backgroundColor);
    const sectionRef = useRef(null);

    const textContentCard = cardItems?.[0];
    const formCard = cardItems?.find((card) => card.__typename === TYPE_MARKETO_FORM);

    // show/hide form - hide form if already submitted within expiry window
    const [isFormSuccess, setIsFormSuccess] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    // handle gated content visibility on successful form submission
    const onFormSuccess = (formId) => {
        scrollToElement(sectionRef);
        setIsRedirecting(true);

        // set session storage value to determine if the user has
        // recently submitted this form
        setValueInStorage(
            formId,
            { expireAt: Date.now() + RESOURCE_GATED_FORM_EXPIRY },
            typeof window !== "undefined" ? window : undefined
        );

        // navigate to thank you page immediately on form submission success
        navigate(successUrl);
        setIsFormSuccess(true);
    };

    // check if the user has recently submitted this form
    // if yes, provide access to the content directly
    const formInvalidCb = () => {
        setIsFormSuccess(false);
        setIsInitialized(true);
    };
    const formValidCb = () => {
        setIsFormSuccess(true);
        setIsInitialized(true);
    };
    useFormSession(formCard?.formId, formValidCb, formInvalidCb);

    const isTwoColumn = textContentCard && formCard;

    const [resourceGatedRedirectionText, resourceAccessFormText] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        RESOURCE_GATED_REDIRECTING_TEXT,
        RESOURCE_GATED_ACCESS_TEXT,
    ]);

    return (
        <section
            ref={sectionRef}
            className={`relative scroll-m-15 ${sectionPaddingClassName} ${sectionColorMapping.bgColor || "bg-white"} ${
                styles.GatedContent
            }`}
            id={customId}
        >
            {backgroundImage?.length > 0 && backgroundImage?.[0]?.resource_type === "image" && (
                <div className="absolute -z-1 left-0 -top-29.5 md:-left-70 md:-top-11.25 lg:left-0 lg:-top-7 h-full">
                    <Media
                        key={backgroundImage?.[0]?.id}
                        link={backgroundImage?.[0]?.secure_url}
                        width={backgroundImage?.[0]?.width}
                        height={backgroundImage?.[0]?.height}
                        className="h-full object-cover"
                    />
                </div>
            )}
            <div className="container">
                <div className="row">
                    {textContentCard && (
                        <div className={`col-12 lg:pr-[65px] ${isTwoColumn ? "md:col-6 lg:col-7" : ""}`}>
                            {textContentCard.title && (
                                <h1
                                    className="title-2" // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: renderSanitizedHtmlContent(textContentCard.title),
                                    }}
                                />
                            )}
                            {textContentCard?.description && (
                                <p className="mt-7.5 text-blue">{textContentCard.description.description}</p>
                            )}
                            {textContentCard?.body?.raw && (
                                <RichText
                                    data={textContentCard}
                                    className={`bodyContent mt-7.5 ${styles.bodyContent}`}
                                />
                            )}
                            {textContentCard?.cta?.map((link) => (
                                <Cta key={link.id} data={link} className="mb-7.5 mt-3.75" />
                            ))}
                            {textContentCard?.media?.[0] && (
                                <div>
                                    <Media
                                        media={textContentCard?.media?.[0]}
                                        className="h-auto max-h-225 max-w-88 rounded-sm md:max-w-none"
                                    />
                                    {textContentCard?.media?.[0]?.mediaCaptionText?.mediaCaptionText && (
                                        <span className="block body text-blue mt-7.5">
                                            {textContentCard?.media?.[0]?.mediaCaptionText?.mediaCaptionText}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {formCard && isInitialized && (
                        <div className={`col-12 ${isTwoColumn ? "md:col-6 lg:col-5 max-md:mt-10" : ""}`}>
                            {/* if form successfully submitted & within expiry - show access now button instead of the form */}
                            {isRedirecting ? (
                                <p className="text-center py-20 lg:py-30 text-3xl">{resourceGatedRedirectionText}</p>
                            ) : (
                                <AccessForm
                                    isFormSuccess={isFormSuccess}
                                    sectionColorMapping={sectionColorMapping}
                                    successUrl={successUrl}
                                    buttonText={resourceAccessFormText}
                                    formCard={formCard}
                                    onFormSuccess={onFormSuccess}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default GatedContent;
